import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const WhatComesNext: React.FC = () => {
    const [question, setQuestion] = useState<{ sequence: string; correctAnswer: string }>({
        sequence: "",
        correctAnswer: ""
    });
    const [options, setOptions] = useState<string[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);


    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Days and Months – Fill in the Blanks Worksheet for Nursery | Mama’s Guidance"; // Set page title
    }, []);

    const shuffleArray = (array: string[]) => array.sort(() => Math.random() - 0.5);

    const generateNewQuestion = () => {
        const startIndex = Math.floor(Math.random() * (daysOfWeek.length - 2));
        const correctAnswer = daysOfWeek[startIndex + 2];

        const sequence = `${daysOfWeek[startIndex]}, ${daysOfWeek[startIndex + 1]}, __?`;

        const optionsSet = new Set<string>();
        optionsSet.add(correctAnswer);

        while (optionsSet.size < 3) {
            const randomDay = daysOfWeek[Math.floor(Math.random() * daysOfWeek.length)];
            optionsSet.add(randomDay);
        }

        setQuestion({ sequence, correctAnswer });
        setOptions(shuffleArray(Array.from(optionsSet))); // Convert Set to Array
        setSelectedAnswer(null);
        setMessage("");
    };


    const checkAnswer = (answer: string) => {
        setSelectedAnswer(answer)
        if (!answer) {
            setMessage("Please select an answer!");
            return;
        }
        if (answer === question.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setMessage("");
                setShowImage(false);

            }, 5000)
        }
        else {
            setMessage("❌ Try Again!")
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Support early learning with the "Fill in the Blanks: Days of the Week and Months" activity on Mamas Guidance—an interactive exercise for nursery kids to complete missing days and months, helping develop basic time concepts and memory skills.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '76%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Days and Months</h1>

                                    <p>
                                        Get your child started on their calendar journey with this fun and interactive <strong>Fill in the Blanks</strong> worksheet! Specially created for nursery learners, this activity helps children <strong>recall and complete the sequence of days and months</strong> with ease.
                                    </p>

                                    <h2>🗓️ What’s in the Worksheet?</h2>
                                    <p>
                                        This worksheet features incomplete sequences like <strong>“Monday, _____, Wednesday” or “January, February, _____”</strong>, allowing children to <strong>fill in the missing day or month</strong>. It’s a simple yet effective way to reinforce order and memorization.
                                    </p>

                                    <h2>🎯 Learning Goals</h2>
                                    <ul>
                                        <li>Recognize and recall the 7 days of the week</li>
                                        <li>Identify the 12 months of the year</li>
                                        <li>Understand the correct sequence of days and months</li>
                                        <li>Develop basic time and calendar awareness</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use This Worksheet</h2>
                                    <ul>
                                        <li>Say the days or months aloud with your child.</li>
                                        <li>Let them identify what’s missing and write it in the blank.</li>
                                        <li>Repeat the sequence with clapping, singing, or actions for fun reinforcement.</li>
                                        <li>Use a calendar or classroom chart as a visual aid.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess the Area</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/colors/match-color"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Match Same Color</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Community Helper</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why It’s Important</h2>
                                    <p>
                                        Knowing the days of the week and months of the year is essential for children’s <strong>routine, planning, and early numeracy skills</strong>. This worksheet builds memory and lays the foundation for time-related concepts.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Days and Months – Fill in the Blanks Worksheet for Nursery</h1>

                                <p>
                                    Get your child started on their calendar journey with this fun and interactive <strong>Fill in the Blanks</strong> worksheet! Specially created for nursery learners, this activity helps children <strong>recall and complete the sequence of days and months</strong> with ease.
                                </p>

                                <h2>🗓️ What’s in the Worksheet?</h2>
                                <p>
                                    This worksheet features incomplete sequences like “Monday, _____, Wednesday” or “January, February, _____”, allowing children to <strong>fill in the missing day or month</strong>. It’s a simple yet effective way to reinforce order and memorization.
                                </p>

                                <h2>🎯 Learning Goals</h2>
                                <ul>
                                    <li>Recognize and recall the 7 days of the week</li>
                                    <li>Identify the 12 months of the year</li>
                                    <li>Understand the correct sequence of days and months</li>
                                    <li>Develop basic time and calendar awareness</li>
                                </ul>

                                <h2>👩‍🏫 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Say the days or months aloud with your child.</li>
                                    <li>Let them identify what’s missing and write it in the blank.</li>
                                    <li>Repeat the sequence with clapping, singing, or actions for fun reinforcement.</li>
                                    <li>Use a calendar or classroom chart as a visual aid.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess the Area</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/colors/match-color"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Same Color</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Community Helper</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why It’s Important</h2>
                                <p>
                                    Knowing the days of the week and months of the year is essential for children’s <strong>routine, planning, and early numeracy skills</strong>. This worksheet builds memory and lays the foundation for time-related concepts.
                                </p>
                            </article>

                            <h2>What Comes Next?</h2>
                            <h3>{question.sequence}</h3>

                            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px", flexWrap: "wrap" }}>
                                {options.map((option, index) => (
                                    <div className="background-blue"
                                        key={index}
                                        onClick={() => checkAnswer(option)}
                                        style={{
                                            backgroundColor: selectedAnswer === option ? "#4CAF50" : "",
                                        }}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>

                            {message && <h3 style={{ marginTop: "20px" }}>{message}</h3>}
                        </div></>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default WhatComesNext;
